import {
  Entity,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { EmptyState } from '@backstage/core-components';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependencyOfComponentsCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  EntityRelationWarning,
  EntitySwitch,
  hasCatalogProcessingErrors,
  hasRelationWarnings,
  isComponentType,
  isKind,
  isApiType,
  isOrphan,
  isResourceType,
} from '@backstage/plugin-catalog';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import { EntityCodeCoverageContent } from '@backstage-community/plugin-code-coverage';
import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';
import {
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
  EntityUserProfileCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Button, Grid } from '@material-ui/core';
import { EntityGithubPullRequestsContent } from '@roadiehq/backstage-plugin-github-pull-requests';

import { aiEntityPage } from '../ai/AiEntityPage';
import { AccountDeleteInProgressCard } from '../aws/AccountDeleteInProgressCard';
import { AccountSealedCard } from '../aws/AccountSealedCard';
import { AccountSupportCard } from '../aws/AccountSupportCard';
import { isAwsAccount, isNotDevAwsAccount } from '@internal/cpe-catalog-common';
import { CostOptimizationCard } from '../aws/CostOptimizationCard';
import { ManagedVpcCard } from '../aws/vpc-card/ManagedVpcCard';
import { AboutCard } from '../aws/about-card/AboutCard';
import { CustomAboutCard } from './CustomAboutCard';
import { BudgetCard } from '../aws/budget-card/BudgetCard';
import { EnterpriseTagsCard } from '../aws/enterprise-tags-card';
import { FoundationManagedKMSCard } from '../aws/kms/KmsCard';
import { ZpaCard } from '../aws/zpa-card/ZpaCard';
import { CommonRolesCard } from '../aws/common-roles/CommonRolesCard';
import { OndemandAdminCard } from '../aws/ondemand-admin-card/OndemandAdminCard';
import { FeatureFlagged } from '@backstage/core-app-api';
import {
  EntityYardstickPage,
  GitHubSimulator,
} from '@internal/yardstick-react';
import { TechDocsRedirectExtension } from '../../addons/techdocsRedirect';
import { FeedbackAddon } from '../../addons/techdocs-feedback/src/plugin';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { EmbeddedExplorer } from '@principal-developer-portal/apollo-explorer-pdp';
import { PatternsFrontendPage } from '@internal/backstage-plugin-patterns-frontend';
import { MetricsAlertingInsightsPage } from '@internal/metrics-alerting-insights';
import { AppConfigProfilesCard } from '@internal/plugin-appconfig-frontend';
import { DeploymentRoleCard } from '../aws/oidc/DeploymentRoleCard';
import { DnsForwarderCard } from '../aws/dns-forwarder/DNSForwarderCard';
import { DisasterRecoveryCard } from '../aws/disaster-recovery/DisasterRecoveryCard';

const isLocal = window.location.hostname.includes('localhost');

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <Mermaid />
      <ReportIssue />
      <FeedbackAddon />
      <TechDocsRedirectExtension />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const codeCoverageAnnotationExists = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['backstage.io/code-coverage']);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <EntityGithubActionsContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <CustomAboutCard variant="gridItem" />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard height={400} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/code-coverage"
      title="Code Coverage"
      if={codeCoverageAnnotationExists}
    >
      <EntityCodeCoverageContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/insights-actions" title="Insights and actions">
      <EntityYardstickPage />
    </EntityLayout.Route>
    {isLocal ? (
      <EntityLayout.Route path="/simulator" title="Simulator">
        <GitHubSimulator />
      </EntityLayout.Route>
    ) : null}
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/code-coverage"
      title="Code Coverage"
      if={codeCoverageAnnotationExists}
    >
      <EntityCodeCoverageContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/insights-actions" title="Insights and actions">
      <EntityYardstickPage />
    </EntityLayout.Route>
    {isLocal ? (
      <EntityLayout.Route path="/simulator" title="Simulator">
        <GitHubSimulator />
      </EntityLayout.Route>
    ) : null}
  </EntityLayout>
);

const libraryEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependents" title="Dependents">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item>
          <EntityDependencyOfComponentsCard title="Components using this library" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/code-coverage"
      title="Code Coverage"
      if={codeCoverageAnnotationExists}
    >
      <EntityCodeCoverageContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/insights-actions" title="Insights and actions">
      <EntityYardstickPage />
    </EntityLayout.Route>
    {isLocal ? (
      <EntityLayout.Route path="/simulator" title="Simulator">
        <GitHubSimulator />
      </EntityLayout.Route>
    ) : null}
    <EntityLayout.Route path="/adoption" title="Pattern Adoption">
      <PatternsFrontendPage />
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/code-coverage"
      title="Code Coverage"
      if={codeCoverageAnnotationExists}
    >
      <EntityCodeCoverageContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/insights-actions" title="Insights and actions">
      <EntityYardstickPage />
    </EntityLayout.Route>
    {isLocal ? (
      <EntityLayout.Route path="/simulator" title="Simulator">
        <GitHubSimulator />
      </EntityLayout.Route>
    ) : null}
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('library')}>
      {libraryEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const openapiEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/insights-actions" title="Insights and actions">
      <EntityYardstickPage />
    </EntityLayout.Route>
    {isLocal ? (
      <EntityLayout.Route path="/simulator" title="Simulator">
        <GitHubSimulator />
      </EntityLayout.Route>
    ) : null}
  </EntityLayout>
);

//  This Graphql page shows EmbeddedExplorer in its definition

const graphqlEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EmbeddedExplorer />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    {isLocal ? (
      <EntityLayout.Route path="/simulator" title="Simulator">
        <GitHubSimulator />
      </EntityLayout.Route>
    ) : null}
  </EntityLayout>
);

const asyncEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    {isLocal ? (
      <EntityLayout.Route path="/simulator" title="Simulator">
        <GitHubSimulator />
      </EntityLayout.Route>
    ) : null}
  </EntityLayout>
);

const apiPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isApiType('openapi')}>
      {openapiEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isApiType('graphql')}>
      {graphqlEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isApiType('asyncapi')}>
      {asyncEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard
            entityFilterKind={[
              'Domain',
              'System',
              'Component',
              'API',
              'Template',
              'Resource',
              'Salesforce',
            ]}
            hideRelationsToggle
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard
            entityFilterKind={[
              'Domain',
              'System',
              'Component',
              'API',
              'Template',
              'Resource',
              'Salesforce',
            ]}
            relationsType="direct"
            hideRelationsToggle
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <CustomAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
    <EntityLayout.Route path="/insights-actions" title="Insights and actions">
      <EntityYardstickPage />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const featuresContent = (
  <Grid container alignContent="space-between">
    <Grid item xs={12} md={6}>
      <Grid container direction="column">
        <Grid item xs={12}>
          <FoundationManagedKMSCard variant="gridItem" />
        </Grid>
        <Grid item xs={12}>
          <DisasterRecoveryCard variant="gridItem" />
        </Grid>
      </Grid>
    </Grid>
    <Grid item xs={12} md={6}>
      <DeploymentRoleCard />
    </Grid>
  </Grid>
);

const securityContent = (
  <Grid container alignContent="space-between">
    <EntitySwitch>
      <EntitySwitch.Case if={isNotDevAwsAccount}>
        <Grid item xs={12} md={6}>
          <OndemandAdminCard variant="gridItem" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <Grid item xs={12} md={6}>
      <CommonRolesCard variant="gridItem" />
    </Grid>
  </Grid>
);

const networkingContent = (
  <Grid container alignContent="space-between">
    <Grid item xs={12} md={6}>
      <ManagedVpcCard variant="gridItem" />
    </Grid>
    <Grid item xs={12} md={6}>
      <Grid container direction="column">
        <Grid item xs={12}>
          <ZpaCard variant="gridItem" />
        </Grid>
        <Grid item xs={12}>
          <DnsForwarderCard variant="gridItem" />
        </Grid>
      </Grid>
    </Grid>
  </Grid>
);

const accountOverviewContent = (
  <Grid container spacing={3} alignContent="stretch">
    <Grid item md={6}>
      <AboutCard variant="gridItem" />
    </Grid>
    <Grid item md={6}>
      <EnterpriseTagsCard variant="gridItem" />
    </Grid>
    <EntitySwitch>
      <EntitySwitch.Case if={isAwsAccount}>
        <Grid item xs={12} md={6}>
          <BudgetCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <CostOptimizationCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <Grid item md={6}>
      <AccountSupportCard variant="gridItem" />
    </Grid>
  </Grid>
);

const AccountPageContainer = (props: { ele: JSX.Element }) => {
  return (
    <>
      <AccountDeleteInProgressCard />
      <AccountSealedCard />
      {props.ele}
    </>
  );
};

const accountEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <AccountPageContainer ele={accountOverviewContent} />
    </EntityLayout.Route>
    <EntityLayout.Route path="/features" title="Features" if={isAwsAccount}>
      <AccountPageContainer ele={featuresContent} />
    </EntityLayout.Route>
    <EntityLayout.Route path="/networking" title="Networking" if={isAwsAccount}>
      <AccountPageContainer ele={networkingContent} />
    </EntityLayout.Route>
    <EntityLayout.Route path="/security" title="Security" if={isAwsAccount}>
      <AccountPageContainer ele={securityContent} />
    </EntityLayout.Route>
    <EntityLayout.Route path="/app-config" title="Flags" if={isAwsAccount}>
      <AppConfigProfilesCard />
    </EntityLayout.Route>
    <EntityLayout.Route path="/insights-actions" title="Insights and actions">
      <EntityYardstickPage />
    </EntityLayout.Route>
  </EntityLayout>
);

const resourcePage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isResourceType('account')}>
      {accountEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const templatePage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/code-coverage"
      title="Code Coverage"
      if={codeCoverageAnnotationExists}
    >
      <EntityCodeCoverageContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/insights-actions" title="Insights and actions">
      <EntityYardstickPage />
    </EntityLayout.Route>
    {isLocal ? (
      <EntityLayout.Route path="/simulator" title="Simulator">
        <GitHubSimulator />
      </EntityLayout.Route>
    ) : null}
    <FeatureFlagged with="pattern-adoption-enabled">
      <EntityLayout.Route path="/adoption" title="Pattern Adoption">
        <PatternsFrontendPage />
      </EntityLayout.Route>
    </FeatureFlagged>
    <EntityLayout.Route
      path="/metrics-alerting-insights"
      title="Metrics and alerting"
    >
      <MetricsAlertingInsightsPage />
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourcePage} />
    <EntitySwitch.Case if={isKind('template')} children={templatePage} />
    <EntitySwitch.Case if={isKind('ai')} children={aiEntityPage} />
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
